import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { postSignAgreements, SignAgreementsDto, ApiBasket } from 'src/api/basketService';
import { postPaymentProcess } from 'src/api/paymentService/requests';
import { PaymentProcessResponse } from 'src/api/paymentService/types';
import { syncBasket } from 'src/utils/sync-basket';

import { queryRetry } from '../query-retry';
import { postCreateBookingNoAdvanceFee } from '../../api/createBookingNoAdvanceFee/requests';

import { useGetBasketById } from './use-get-basket-by-id';

type SubmitAgreements = {
    basketId: string;
    agreementsData: SignAgreementsDto;
    updatedAt: string;
};

const usePostSubmitAgreements = () =>
    useMutation<ApiBasket, AxiosError, SubmitAgreements>({
        mutationKey: ['submit-agreements'],
        mutationFn: async ({ basketId, agreementsData, updatedAt }) => {
            const basket = await postSignAgreements(basketId, agreementsData, updatedAt);
            return basket;
        },
        retry: queryRetry,
        onSuccess: (basket) => syncBasket(basket),
    });

export const useSubmitAgreementsAndPay = () => {
    const { refetch } = useGetBasketById();
    const { mutateAsync, data, error } = usePostSubmitAgreements();

    return useMutation<
        { basket: ApiBasket; payment: PaymentProcessResponse },
        AxiosError,
        SubmitAgreements
    >({
        mutationKey: ['submit-agreements', 'paid'],
        mutationFn: async ({ basketId, agreementsData, updatedAt }) => {
            if (error) throw error;

            let basket = data;

            if (!basket) {
                basket = await mutateAsync({ basketId, agreementsData, updatedAt });
            }

            const payment = await postPaymentProcess(basketId);
            return { basket, payment };
        },
        retry: queryRetry,
        onSuccess: ({ basket }) => {
            syncBasket(basket);
        },
        onError: () => {
            refetch();
        },
    });
};

export const useSubmitAgreementsAndVerify = () => {
    const { mutateAsync, data, error } = usePostSubmitAgreements();

    return useMutation<
        { basket: ApiBasket; bookingRecordId: string },
        AxiosError,
        SubmitAgreements
    >({
        mutationKey: ['submit-agreements', 'no-pay'],
        mutationFn: async ({ basketId, agreementsData, updatedAt }) => {
            if (error) throw error;

            let basket = data;

            if (!basket) {
                basket = await mutateAsync({ basketId, agreementsData, updatedAt });
            }

            const bookingRecordId = await postCreateBookingNoAdvanceFee({ basket_id: basket.id });
            return { basket, bookingRecordId };
        },
        retry: queryRetry,
        onSuccess: ({ basket }) => {
            syncBasket(basket);
        },
    });
};
